import { createAsyncThunk } from '@reduxjs/toolkit';
import { CategoriesAPI, FiltersAPI } from '@/api';
import { formatApiError } from '@/utils';
import { IApiError } from '@/models/apiError.model';
import { CATEGORIES_SLICE_NAME } from './models';
import {
  ICategory,
  ICategoryDetails,
  ICategoryStats,
  IDeleteCategoryResponse,
  ISubCategoriesResponse,
  IEditCategoryDTO,
  ICreateCategoryDTO,
  ICreatePopularCategoriesDTO,
  IUpdateFiltersCategoryDTO,
  ICategoriesAutocompleteDTO,
  IGetCategoryStatsDTO,
} from '@/models/categories.model';
import {
  ICreateFilterDTO,
  ICreateFilterValueDTO,
  IDeleteFilterValueDTO,
  IEditFilterValueDTO,
  IFilterDetailsResponse,
  IFiltersResponse,
  IFilterValue,
  IFilterValuesResponse,
  IGetFiltersDTO,
  IGetFiltersListDTO,
  IGetFilterValuesDTO,
  IImportSFiltersDTO,
} from '@/models/filters.model';

export const updateCategoryFilters = createAsyncThunk<
  ICategoryDetails,
  { data: IUpdateFiltersCategoryDTO; id?: string },
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/updateFiltersCategory`,
  async ({ data, id }) => {
    const response = await CategoriesAPI.updateFiltersCategory(data, id);
    return response.data.data;
  },
  { serializeError: formatApiError },
);
export const getCategoryStats = createAsyncThunk<
  ICategoryStats,
  IGetCategoryStatsDTO,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/getCategoryStats`,
  async (dto) => {
    const response = await CategoriesAPI.getCategoryStats(dto);
    return response.data.data;
  },
  { serializeError: formatApiError },
);

export const getPopularCategories = createAsyncThunk<ICategory[], void, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/getPopularCategories`,
  async () => {
    const response = await CategoriesAPI.getPopularCategories();
    return response.data.data;
  },
  { serializeError: formatApiError },
);

export const createPopularCategories = createAsyncThunk<
  ICategory[],
  ICreatePopularCategoriesDTO,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/createPopularCategories`,
  async (data) => {
    const response = await CategoriesAPI.createPopularCategories(data);
    return response.data.data;
  },
  { serializeError: formatApiError },
);

export const getCategories = createAsyncThunk<ICategory[], number | undefined, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/getCategories`,
  async (limit?: number) => {
    const response = await CategoriesAPI.getCategories(limit);
    return response.data.data;
  },
  { serializeError: formatApiError },
);

export const getCategoriesAutocomplete = createAsyncThunk<
  ICategory[],
  ICategoriesAutocompleteDTO,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/getCategoriesAutocomplete`,
  async (data) => {
    const response = await CategoriesAPI.getCategoriesAutocomplete(data);
    return response.data.data;
  },
  { serializeError: formatApiError },
);

export const getFilterValues = createAsyncThunk<
  IFilterValuesResponse,
  IGetFilterValuesDTO,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/getFilterValues`,
  async (data) => {
    const response = await FiltersAPI.getFilterValues(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getSubCategories = createAsyncThunk<ISubCategoriesResponse, string, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/getSubCategories`,
  async (id) => {
    const response = await CategoriesAPI.getSubCategories(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getCategoryDetails = createAsyncThunk<ICategoryDetails, string, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/getCategoryDetails`,
  async (id) => {
    const response = await CategoriesAPI.getCategoryDetails(id);
    return response.data.data;
  },
  { serializeError: formatApiError },
);

export const deleteCategory = createAsyncThunk<IDeleteCategoryResponse, string, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/deleteCategory`,
  async (id) => {
    const response = await CategoriesAPI.deleteCategory(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const toggleActiveCategory = createAsyncThunk<
  IDeleteCategoryResponse,
  string,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/toggleActiveCategory`,
  async (id) => {
    const response = await CategoriesAPI.toggleActiveCategory(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const editCategory = createAsyncThunk<
  ICategoryDetails,
  { id: string; data: IEditCategoryDTO },
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/editCategory`,
  async ({ id, data }) => {
    const response = await CategoriesAPI.editCategory(id, data);
    return response.data.data;
  },
  { serializeError: formatApiError },
);

export const createCategory = createAsyncThunk<
  ICategoryDetails,
  ICreateCategoryDTO,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/createCategory`,
  async (data) => {
    const response = await CategoriesAPI.createCategory(data);
    return response.data.data;
  },
  { serializeError: formatApiError },
);

export const getCategoryFiltersList = createAsyncThunk<
  IFiltersResponse,
  IGetFiltersListDTO | undefined,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/getFiltersList`,
  async (data) => {
    const response = await FiltersAPI.getFiltersList(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getFilters = createAsyncThunk<
  IFiltersResponse,
  IGetFiltersDTO | undefined,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/getFilters`,
  async (data) => {
    const response = await FiltersAPI.getFilters(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getFilterData = createAsyncThunk<IFilterDetailsResponse, string, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/getFilterData`,
  async (id) => {
    const response = await FiltersAPI.getFilterData(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const deleteFilter = createAsyncThunk<IFilterDetailsResponse, string, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/deleteFilter`,
  async (id) => {
    const response = await FiltersAPI.deleteFilter(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const deleteFilterValue = createAsyncThunk<void, IDeleteFilterValueDTO, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/deleteFilterValue`,
  async (data) => {
    const response = await FiltersAPI.deleteFilterValue(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const editFilterValue = createAsyncThunk<IFilterValue, IEditFilterValueDTO, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/editFilterValue`,
  async (data) => {
    const response = await FiltersAPI.editFilterValue(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const createFilterValue = createAsyncThunk<
  IFilterValue,
  ICreateFilterValueDTO,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/createFilterValue`,
  async (data) => {
    const response = await FiltersAPI.createFilterValue(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const createFilter = createAsyncThunk<
  IFilterDetailsResponse,
  ICreateFilterDTO,
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/createFilter`,
  async (data) => {
    const response = await FiltersAPI.createFilter(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const editFilter = createAsyncThunk<
  IFilterDetailsResponse,
  { id: string; data: ICreateFilterDTO },
  { serializedErrorType: IApiError }
>(
  `${CATEGORIES_SLICE_NAME}/editFilter`,
  async ({ id, data }) => {
    const response = await FiltersAPI.editFilter(id, data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const importFilters = createAsyncThunk<void, IImportSFiltersDTO, { serializedErrorType: IApiError }>(
  `${CATEGORIES_SLICE_NAME}/importFilters`,
  async (data) => {
    const response = await FiltersAPI.importFilters(data);
    return response.data;
  },
  { serializeError: formatApiError },
);
